import { useEffect, useState, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { authVersionState, meState } from '../state.js';
import { setCurrentUsername } from '../api.js';
import { authApi } from '../authApi.js';
import { ClientPicker, ClientAvatar } from './ClientPicker.js';
import { AgioLogo } from './AgioLogo.js';
import { ThemeToggle } from './ThemeToggle.js';
import { TextSizeControl } from './TextSizeControl.js';
import { useApi } from '../hooks.js';
import type { ClientInfo } from '@agio/core';
import { cn } from '../utils.js';

function Nav({
  to,
  children,
  /** Extra path prefixes this link owns — the admin area is one link over many
   *  sections, and it should stay lit on all of them. */
  covers,
}: {
  to: string;
  children: ReactNode;
  covers?: string[];
}) {
  const { pathname } = useLocation();
  const covered = covers?.some((p) => pathname === p || pathname.startsWith(`${p}/`)) ?? false;
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        cn(
          'rounded-md px-3 py-2 text-sm transition-colors',
          isActive || covered
            ? 'bg-muted font-semibold text-foreground'
            : 'text-muted-foreground hover:bg-muted/60',
        )
      }
    >
      {children}
    </NavLink>
  );
}

/** True once the page is scrolled past the top (drives the header glow). */
function useScrolled() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return scrolled;
}

export function Layout({ children }: { children: ReactNode }) {
  const scrolled = useScrolled();
  const me = useRecoilValue(meState);
  const bumpAuth = useSetRecoilState(authVersionState);
  const { data: myClients } = useApi<ClientInfo[]>(me?.role === 'viewer' ? '/clients' : null);
  const { pathname } = useLocation();
  if (!me) return null;
  const viewerClient = me.role === 'viewer' ? myClients?.[0] : undefined;
  const isAdminPage = pathname.startsWith('/admin');

  return (
    <div className="min-h-screen">
      <header
        className={cn(
          'sticky top-0 z-10 border-b bg-background/70 backdrop-blur transition duration-300',
          scrolled ? 'border-border shadow-(--shadow-card)' : 'border-transparent',
        )}
      >
        {/* Wraps on a phone. Everything here on one unwrapped row was wider than a
            390px screen, which pushed EVERY page sideways — tables and all. */}
        <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-x-6 gap-y-2 px-4 py-3 sm:px-6">
          <AgioLogo size="sm" className="shrink-0 opacity-90" />
          <nav className="flex min-w-0 flex-wrap items-center gap-1">
            <Nav to="/">תיק השקעות</Nav>
            {me.role === 'uber' && (
              <>
                <Nav
                  to="/admin/monthly"
                  covers={['/admin/policy', '/admin/new-client', '/admin/benchmark', '/admin/clients',
                           '/admin/funds', '/admin/jobs', '/admin/snapshots', '/admin/issues', '/admin/export']}
                >
                  ניהול נתונים
                </Nav>
                <Nav to="/admin/users">משתמשים</Nav>
              </>
            )}
          </nav>
          <div className="ms-auto flex flex-wrap items-center gap-2 sm:gap-3">
            {me.role === 'uber' && (
              <span className="flex items-center gap-2 rounded-lg border border-border bg-secondary/50 py-1 pe-1.5 ps-3">
                <span className="text-xs font-semibold text-muted-foreground">ניהול</span>
                <ClientPicker />
              </span>
            )}
            {viewerClient && (
              <span className="flex items-center gap-2 rounded-full border border-border bg-secondary py-1 pe-3 ps-1 text-sm">
                <ClientAvatar name={viewerClient.display_name_he} logoUrl={viewerClient.logo_url} size={24} />
                {viewerClient.display_name_he}
              </span>
            )}
            <span className="hidden text-sm text-muted-foreground sm:block">{me.displayName}</span>
            <TextSizeControl />
            <ThemeToggle />
            <button
              className="rounded-md border border-border px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted"
              onClick={async () => {
                // Ends the session and nothing else — no password reset, no
                // re-enrolment. Signing back in takes the password and a code.
                await authApi.logout().catch(() => {});
                setCurrentUsername(null);
                bumpAuth((v) => v + 1);
              }}
            >
              יציאה
            </button>
          </div>
        </div>
      </header>
      <main className={isAdminPage ? 'light-scope min-h-screen' : undefined}>
        <div className="mx-auto max-w-7xl px-6 py-8">{children}</div>
      </main>
    </div>
  );
}
